import { useEffect, useState } from 'react';
import clsx from 'clsx';

import Typography from 'components/CncUI/Typography';
import Button from 'components/CncUI/Button';
import Alert from 'components/CncUI/Alert';

import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import LeftArrow from '@material-ui/icons/KeyboardArrowLeftRounded';
import OktaLogo from 'images/createAccount/OktaLogo.png';

import Instructions from './Instructions';
import QRCode from './QRCode';
import SecretKey from './SecretKey';

import useOktaVerifyFactorRequest from '../hooks/useOktaVerifyFactorRequest';
import Logger from 'utilities/logger';

const useStyles = makeStyles(({ palette, breakpoints }) => ({
  root: {
    display: 'flex',
    width: 484,
    backgroundColor: palette.cncUI.white,
    boxShadow: '0 0 5px 3px rgba(0, 0, 0, 0.1)',
    marginTop: 50,
    marginLeft: 'auto',
    marginRight: 'auto',
    padding: '40px 56px 80px 50px',
    alignContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    [breakpoints.down('sm')]: {
      width: '100%',
      marginTop: 0,
      paddingLeft: 0,
      paddingRight: 0,
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
      borderBottom: `2px solid ${palette.cncUI.default['-3']}`,
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):after': {
      borderBottom: `2px solid ${palette.cncUI.default['-3']}`,
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: palette.cncUI.default['-3'],
    },
    '& .MuiAccordion-root:before': {
      backgroundColor: 'transparent',
    },
    '& .MuiGrid-grid-xs-12': {
      width: '100%',
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    alignItems: 'center',
    '& h1': {
      color: palette.cncUI.default['-9'],
    },
    '& p': {
      color: palette.cncUI.primary.gray['+0'],
    },
  },
  button: {
    width: 210,
    height: 40,
    marginTop: 31,
    marginBottom: 30,
    padding: 0,
    [breakpoints.down('sm')]: {
      width: 300,
      marginTop: 25,
      marginBottom: 40,
    },
  },
  contained: {
    '& span': {
      height: '100%',
      borderRadius: 2,
    },
  },
  leftArrow: {
    borderRadius: '50%',
    backgroundColor: `${palette.cncUI.default.background} !important`,
    color: palette.secondary.main,
    marginRight: 8,
  },
  backButton: {
    textTransform: 'none',
  },
  title: {
    fontWeight: 400,
    color: palette.cncUI.default['-9'],
    marginTop: 16,
  },
  subTitle: {
    width: 357,
    color: '#5c5c5c !important',
    [breakpoints.down('sm')]: {
      width: 335,
      padding: '0 18px',
    },
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
  },
  error: {
    width: 344,
    marginBottom: -16,
    marginTop: 8,
    fontWeight: 700,
  },
}));

const OktaVerify = props => {
  const classes = useStyles();
  const { setStep, state: stepState, changeState, isMobile } = props;
  const { tokenData } = stepState;
  const logger = Logger('accountCreation.oktaVerify');
  const factorRequest = useOktaVerifyFactorRequest();
  const [code, setCode] = useState('');
  const [state, setState] = useState({
    section: 'instructions',
    title: 'Okta Verify setup',
    button: '_initial_',
    error: '',
    backButton: 'Back',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [state.section]);

  const modifyState = data => setState(state => ({ ...state, ...data }));
  const action = () => {
    if (state.section === 'instructions') {
      factorRequest
        .enroll(tokenData.user.id)
        .then(factor => {
          modifyState({
            factor,
            section: isMobile ? 'secretKey' : 'qr_code',
            title: isMobile ? 'Secret key' : 'Scan QR Code',
            subTitle: isMobile
              ? 'Copy secret key below, then paste the key into the secret key area in the Okta Verify application'
              : 'Launch the Okta Verify application on your mobile device and Select "Add Account" to scan the QR code.',
            button: 'Verify code',
            backButton: 'Back to instructions',
          });
        })
        .catch(err => {
          logger.error('Enroll Okta Verify', err);
          modifyState({ error: err.message });
        });
    } else if (state.section === 'qr_code' || state.section === 'secretKey') {
      factorRequest
        .activate(tokenData.user.id, state.factor.id, code)
        .then(() => {
          changeState({ okta: true, adding: 'okta' });
          modifyState({ backButton: 'Back' });
          setStep('AccountCreated');
        })
        .catch(err => {
          logger.error('Activate Okta Verify', err); // TODO handle the errors
          modifyState({ error: err.message });
        });
    }
  };

  const handleBack = () => {
    switch (state.section) {
      case 'secretKey':
      case 'qr_code':
        modifyState({
          section: 'instructions',
          title: 'Okta Verify setup',
          button: isMobile ? 'Get secret key' : 'Go to QR code',
          backButton: 'Back',
        });
        break;
      case 'instructions':
      default:
        setStep('AccountCreated');
        break;
    }
  };

  return (
    <Paper className={classes.root} id="OktaVerifyRoot">
      <Grid container spacing={isMobile ? 0 : 1} className={classes.container}>
        <Grid item xs={12}>
          <img src={OktaLogo} width={64} height={64} alt="Okta Verify" />
        </Grid>
        <Grid item xs={12}>
          <Typography
            variant="h4"
            className={classes.title}
            id="OktaVerifyTitle"
          >
            {state.title}
          </Typography>
        </Grid>
        <Grid item xs={12} className={classes.center}>
          <Typography variant="body2" className={classes.subTitle}>
            {state.subTitle}
          </Typography>
        </Grid>
        {state.error && (
          <Grid item xs={12} className={clsx(classes.error, classes.center)}>
            <Alert fullWidth error id="OktaVerifyError">
              {state.error.replace('GraphQL error: ', '')}
            </Alert>
          </Grid>
        )}
        {state.section === 'instructions' && (
          <Instructions isMobile={isMobile} />
        )}
        {state.section === 'qr_code' && (
          <QRCode
            code={code}
            factor={state.factor}
            setCode={setCode}
            modifyState={modifyState}
          />
        )}
        {state.section === 'secretKey' && (
          <SecretKey
            code={code}
            factor={state.factor}
            setCode={setCode}
            modifyState={modifyState}
            isMobile={isMobile}
          />
        )}
        {state.button && (
          <Grid item xs={12}>
            <Button
              className={clsx(classes.button, classes.contained)}
              onClick={() => action()}
              id={`OktaVerifyAction${
                state.button === '_initial_'
                  ? isMobile
                    ? 'GetSecretKey'
                    : 'GoToQRCode'
                  : state.button.replace(/\s+/g, '')
              }Button`}
            >
              {state.button === '_initial_'
                ? isMobile
                  ? 'Get secret key'
                  : 'Go to QR code'
                : state.button}
            </Button>
          </Grid>
        )}
        <Grid item xs={12}>
          <Button
            tertiary
            className={classes.backButton}
            onClick={handleBack}
            id="OktaVerifyBackButton"
          >
            <LeftArrow className={classes.leftArrow} />
            {state.backButton}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default OktaVerify;
